import {TableCell, TableHead, TableRow, Theme} from '@mui/material';
import {LoadingSpinner, useLocalStorage} from '@wallaby-ui/core';
import {columnWidthPatch, getDefaultMTOptions, MaterialTableWrapper, useFormatters} from '@wallaby-ui/document';
import {Column} from 'material-table';
import * as React from 'react';
import {useMemo} from 'react';

import {ValueCellWithCopy, useUzisTableStyles} from './UzisReportTableCommon';
import {UzisReportVResult} from './uzisreporttypes';

import type {SystemStyleObject} from '@mui/system';

type Props = {
  data: UzisReportVResult | undefined;
  isLoading: boolean | undefined;
  storageIdPrefix: string;
};

type TableRowT = {
  colA: string;
  colB: number | string;
  col1: number;
  col2: number;
  col3: number;
  col4: number;
  col5: number;
  col6: number;
};

const getLeafCellStyle = (props: any, columnDef: any, exactWidth?: string) => {
  const cd = columnWidthPatch(columnDef);
  const {width} = cd.tableData;

  return {
    ...(props.headerStyle ?? {}),
    ...(cd.headerStyle ?? {}),
    boxSizing: 'border-box',
    width: exactWidth || width,
    maxWidth: exactWidth || cd.maxWidth,
    minWidth: exactWidth || cd.minWidth,
  } as React.CSSProperties;
};

type VerticalInsetFrom = 'top' | 'bottom' | 'top_bottom';

export const verticalBorder = (
  side: 'left' | 'right',
  insetFrom: VerticalInsetFrom = 'top',
  offsetPx: number = 10
): SystemStyleObject<Theme> => ({
  content: '""',
  position: 'absolute',
  ...(insetFrom === 'top'
    ? {top: `${offsetPx}px`, bottom: 0}
    : insetFrom === 'bottom'
    ? {top: 0, bottom: `${offsetPx}px`}
    : {top: `${offsetPx}px`, bottom: `${offsetPx}px`}),
  [side]: 0,
  width: '2px',
  backgroundColor: (theme) => theme.palette.divider,
  pointerEvents: 'none',
});
const topLeftBorder = verticalBorder('left', 'top');
const topRightBorder = verticalBorder('right', 'top');
const bottomLeftBorder = verticalBorder('left', 'bottom');
const bottomRightBorder = verticalBorder('right', 'bottom');
const leftBorder = verticalBorder('left', 'top_bottom');

export const CustomTableVHeader: React.FC<any> = (props) => {
  const cols = props.columns
    .filter((c: any) => !c.hidden && !(c.tableData.groupOrder > -1))
    .sort((a: any, b: any) => a.tableData.columnOrder - b.tableData.columnOrder);

  const [colA, colB, c1, c2, c3, c4, c5, c6] = cols;
  const {t} = useFormatters();

  return (
    <TableHead>
      <TableRow>
        <TableCell style={getLeafCellStyle(props, colA, '150px')} rowSpan={2} />
        <TableCell align='center' style={getLeafCellStyle(props, colB)} rowSpan={2}>
          {t('reports.uzis.common.rowNumber')}
        </TableCell>

        <TableCell
          colSpan={4}
          align='center'
          sx={{
            ...props.headerStyle,
            'position': 'relative',
            '&::before': topLeftBorder,
          }}
        >
          {t('reports.uzis.tableV.hospitalization')}
        </TableCell>

        <TableCell colSpan={1} align='center' sx={{...props.headerStyle, 'position': 'relative', '&::before': topLeftBorder}}>
          {t('reports.uzis.tableV.ambulatoryCare')}
        </TableCell>

        <TableCell align='center' sx={{...getLeafCellStyle(props, c6), 'position': 'relative', '&::before': leftBorder}} rowSpan={2}>
          {t('reports.uzis.tableV.plasmaInterpretations')}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell align='center' sx={{...getLeafCellStyle(props, c1), 'position': 'relative', '&::before': bottomLeftBorder}}>
          {t('reports.uzis.tableV.performance.05751')}
        </TableCell>
        <TableCell align='center' style={getLeafCellStyle(props, c2)}>
          {t('reports.uzis.tableV.performance.05753')}
        </TableCell>
        <TableCell align='center' style={getLeafCellStyle(props, c3)}>
          {t('reports.uzis.tableV.performance.05755')}
        </TableCell>
        <TableCell align='center' sx={{...getLeafCellStyle(props, c4), 'position': 'relative', '&::after': bottomRightBorder}}>
          {t('reports.uzis.tableV.performance.05755other')}
        </TableCell>
        <TableCell align='center' sx={{...getLeafCellStyle(props, c5), 'position': 'relative', '&::before': bottomLeftBorder}}>
          {t('reports.uzis.tableV.performance.05752')}
        </TableCell>
      </TableRow>
    </TableHead>
  );
};

const CustomHeader = (props: any) => <CustomTableVHeader {...props} />;

export const UzisReportTableV: React.FC<Props> = ({data, isLoading = true, storageIdPrefix}) => {
  const muiStyles = useUzisTableStyles('0px');
  const {formatFloat, t} = useFormatters();
  const [defaultTableScroll, setDefaultTableScroll] = useLocalStorage(`${storageIdPrefix}.tableV.scroll`, 0);

  const tableData: TableRowT[] = useMemo(() => {
    if (!data) return [];

    return [
      {
        colA: 'a',
        colB: 'b',
        col1: 1,
        col2: 2,
        col3: 3,
        col4: 4,
        col5: 5,
        col6: 6,
      },
      {
        colA: t('total'),
        colB: 51,
        col1: data.proc51,
        col2: data.proc53,
        col3: data.proc55,
        col4: data.proc55other,
        col5: data.proc52,
        col6: data.iopdc,
      },
    ];
  }, [data, t]);

  const columns: Column<TableRowT>[] = useMemo(
    () => [
      {
        field: 'colA',
        title: 'a',
        render: (rowData) => <span style={{fontWeight: 500}}>{rowData.colA}</span>,
        sorting: false,
        cellStyle: {width: '150px', textAlign: 'center'},
        headerStyle: {width: '150px', textAlign: 'center'},
      },
      {
        field: 'colB',
        title: 'b',
        render: (rowData) => {
          return (
            <ValueCellWithCopy
              value={rowData.colB}
              formatInteger={formatFloat}
              showCopyButton={rowData?.tableData?.id != null && rowData?.tableData?.id > 0}
            />
          );
        },
        sorting: false,
        cellStyle: {width: '60px', textAlign: 'center', color: '#9e9e9e'},
        headerStyle: {width: '60px', textAlign: 'center'},
      },
      {
        field: 'col1',
        title: '1',
        render: (rowData) => (
          <ValueCellWithCopy
            value={rowData.col1}
            formatInteger={formatFloat}
            showCopyButton={rowData?.tableData?.id != null && rowData?.tableData?.id > 0}
          />
        ),
        sorting: false,
        cellStyle: {textAlign: 'center', minWidth: '120px'},
      },
      {
        field: 'col2',
        title: '2',
        render: (rowData) => (
          <ValueCellWithCopy
            value={rowData.col2}
            formatInteger={formatFloat}
            showCopyButton={rowData?.tableData?.id != null && rowData?.tableData?.id > 0}
          />
        ),
        sorting: false,
        cellStyle: {textAlign: 'center', minWidth: '120px'},
      },
      {
        field: 'col3',
        title: '3',
        render: (rowData) => (
          <ValueCellWithCopy
            value={rowData.col3}
            formatInteger={formatFloat}
            showCopyButton={rowData?.tableData?.id != null && rowData?.tableData?.id > 0}
          />
        ),
        sorting: false,
        cellStyle: {textAlign: 'center', minWidth: '120px'},
      },
      {
        field: 'col4',
        title: '4',
        render: (rowData) => (
          <ValueCellWithCopy
            value={rowData.col4}
            formatInteger={formatFloat}
            showCopyButton={rowData?.tableData?.id != null && rowData?.tableData?.id > 0}
          />
        ),
        sorting: false,
        cellStyle: {textAlign: 'center', minWidth: '120px'},
      },
      {
        field: 'col5',
        title: '5',
        render: (rowData) => (
          <ValueCellWithCopy
            value={rowData.col5}
            formatInteger={formatFloat}
            showCopyButton={rowData?.tableData?.id != null && rowData?.tableData?.id > 0}
          />
        ),
        sorting: false,
        cellStyle: {textAlign: 'center', minWidth: '120px'},
      },
      {
        field: 'col6',
        title: '6',
        render: (rowData) => (
          <ValueCellWithCopy
            value={rowData.col6}
            formatInteger={formatFloat}
            showCopyButton={rowData?.tableData?.id != null && rowData?.tableData?.id > 0}
          />
        ),
        sorting: false,
        cellStyle: {textAlign: 'center', minWidth: '150px'},
      },
    ],
    [formatFloat]
  );

  return (
    <div css={muiStyles.tableWrapper}>
      {isLoading ? (
        // Render the spinner directly in the flex wrapper — the tableAutoWidth (inline-block, fit-content)
        // wrapper would otherwise collapse it to the left.
        <LoadingSpinner isVisible marginTop={0} wrapperStyles={{flexGrow: 1, alignContent: 'center'}} />
      ) : (
      <div css={muiStyles.tableAutoWidth}>
        <MaterialTableWrapper
          style={{maxHeight: '100%'}}
          defaultScroll={{to: defaultTableScroll}}
          onScroll={(event) => setDefaultTableScroll((event.target as HTMLDivElement).scrollTop)}
          options={{
            ...getDefaultMTOptions(15),
            paging: false,
            rowStyle: (_rowData, index) => ({
              color: index === 0 ? '#9e9e9e' : undefined,
            }),
          }}
          variant='big'
          useMaxHeight
          minColumnWidth={0} // PDH-1589: opt out — custom header + cellStyle widths
          columns={columns}
          data={tableData}
          isLoading={isLoading}
          totalCount={tableData.length}
          components={{
            Header: CustomHeader,
          }}
        />
      </div>
      )}
    </div>
  );
};
